import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogPortal,
  Typography,
} from "@/components/ui";
import {
  getFileIcon,
  getFileNameParts,
  handleDownload,
} from "@/lib/utils/matterFile";
import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import { generatePublicS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { ArrowLeft, Download, Eye, FileArchive, FileText } from "lucide-react";
import type { AccessMatterShareFolderResponseData } from "@/lib/data/requests/matters/matter-share-folder";

interface FileShareViewProps {
  files: AccessMatterShareFolderResponseData[];
  onBack: () => void;
  shareFolderPasscode: string;
  folderLabel?: string;
}

type SharedFileItem = {
  name: string;
  extension: string;
  url: string;
};

const buildSharedFileItems = (
  files: AccessMatterShareFolderResponseData[],
): SharedFileItem[] => {
  return files.map((file) => {
    const name = file.fileName ?? "Untitled file";
    const { fileExtension } = getFileNameParts(name);

    return {
      name,
      extension: fileExtension,
      url: file.presignedUrlKey ?? "",
    };
  });
};

export function FileShareView({
  files,
  onBack,
  shareFolderPasscode,
  folderLabel,
}: FileShareViewProps) {
  const [previewFile, setPreviewFile] = useState<SharedFileItem | null>(null);

  const fileItems = useMemo(() => buildSharedFileItems(files), [files]);

  const collectionLabel = folderLabel || "Shared files";

  const handlePreview = (file: SharedFileItem) => {
    setPreviewFile(file);
  };

  const handlePublicDownload = async (file: SharedFileItem) => {
    const response = await generatePublicS3ItemPreSignedUrlV1(
      "matter-share-folder-file",
    )({
      clientMethod: "get_object",
      key: file.url,
      shareFolderPasscode,
    });
    await handleDownload(file.name, response.data.presignedUrl);
  };

  return (
    <div className="flex h-screen w-full flex-col bg-gradient-to-br from-blue-50 via-white to-indigo-100 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950">
      <Dialog
        open={!!previewFile}
        onOpenChange={(open) => {
          if (!open) setPreviewFile(null);
        }}
      >
        <DialogPortal>
          <DialogContent className="bg-card fixed top-1/2 left-1/2 z-[1000] max-h-[90vh] w-[95vw] max-w-3xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl p-0 shadow-2xl outline-none">
            {previewFile && (
              <FileViewer
                file={{
                  fileName: previewFile.name,
                  presignedURLKey: previewFile.url,
                }}
                fileType="matter-share-folder-file"
                unauthenticated
                shareFolderPasscode={shareFolderPasscode}
              />
            )}
          </DialogContent>
        </DialogPortal>
      </Dialog>

      <header className="dark:bg-card/80 shrink-0 bg-white/60 px-4 py-3 backdrop-blur-sm">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-muted-foreground"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div className="text-right">
            <Typography variant="heading" size="md" className="text-base">
              {collectionLabel}
            </Typography>
            <Typography
              variant="default"
              className="text-muted-foreground text-sm"
            >
              {fileItems.length} {fileItems.length === 1 ? "file" : "files"}{" "}
              shared
            </Typography>
          </div>
        </div>
      </header>

      <div className="dark:bg-card/80 shrink-0 border-b bg-white/60 px-4 pt-6 pb-4 backdrop-blur-sm">
        <div className="mx-auto w-full max-w-4xl">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-3">
              <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
                <FileText className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <Typography
                  variant="default"
                  className="text-primary/70 text-xs font-medium tracking-wide uppercase"
                >
                  Shared portal
                </Typography>

                <Typography variant="heading" size="lg" className="text-xl">
                  Access your shared documents
                </Typography>

                <Typography
                  variant="default"
                  className="text-muted-foreground text-sm"
                >
                  Preview or download the files provided by your legal team.
                  These links expire automatically to keep your information
                  safe.
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 overflow-y-auto px-4 py-6">
        <div className="mx-auto w-full max-w-4xl">
          <section className="space-y-4">
            {fileItems.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
                  <FileArchive className="text-muted-foreground h-10 w-10" />
                  <Typography variant="heading" size="sm" className="text-base">
                    No files available
                  </Typography>
                  <Typography
                    variant="default"
                    className="text-muted-foreground text-sm"
                  >
                    We couldn&apos;t find any shared files linked to this
                    passcode. Please reach out to your legal representative if
                    you expect to see documents here.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              fileItems.map((file, index) => (
                <FileShareListItem
                  key={`${file.name}-${index}`}
                  file={file}
                  onPreview={() => {
                    handlePreview(file);
                  }}
                  onDownload={() => {
                    void handlePublicDownload(file);
                  }}
                />
              ))
            )}
          </section>
        </div>
      </main>

      <footer className="dark:bg-card/90 shrink-0 border-t bg-white/80 px-4 py-4 backdrop-blur-sm">
        <div className="text-muted-foreground mx-auto flex w-full max-w-4xl flex-col gap-1 text-center text-xs md:flex-row md:items-center md:justify-between md:text-left">
          <span>
            Having trouble accessing a document? Contact your legal
            representative.
          </span>
          <span>Links expire automatically to protect your privacy.</span>
        </div>
      </footer>
    </div>
  );
}

interface FileShareListItemProps {
  file: SharedFileItem;
  onPreview: () => void;
  onDownload: () => void;
}

function FileShareListItem({
  file,
  onPreview,
  onDownload,
}: FileShareListItemProps) {
  const Icon = getFileIcon(file.name);
  const extensionLabel = file.extension
    ? file.extension.toUpperCase()
    : undefined;

  return (
    <Card className="border-0 shadow-sm transition-shadow hover:shadow-lg">
      <CardContent className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:gap-6">
        <div className="flex items-start gap-3 md:items-center">
          <div className="bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-xl">
            <Icon className="h-6 w-6" />
          </div>
          <div className="space-y-2">
            <div className="flex flex-col">
              <Typography variant="heading" size="sm" className="text-base">
                {file.name}
              </Typography>
              <Typography
                variant="default"
                className="text-muted-foreground text-sm"
              >
                Shared
              </Typography>
            </div>
            <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
              {extensionLabel && (
                <Badge variant="outline" className="uppercase">
                  {extensionLabel}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-2 md:flex-row md:justify-end">
          <Button
            variant="outline"
            onClick={onPreview}
            className="flex-1 md:flex-initial"
          >
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button onClick={onDownload} className="flex-1 md:flex-initial">
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
